import {
  Alert,
  Field,
  HStack,
  NativeSelect,
  Text,
  SimpleGrid,
  Flex,
  chakra,
  Select,
  Spinner,
  type ListCollection,
  GridItem,
} from '@chakra-ui/react'
import { createListCollection } from '@chakra-ui/react'
import { useFormik } from 'formik'
import { useEffect, useState, useMemo } from 'react'

import {
  ProfileSettingsSchema,
  INITIAL_PROFILE_SETTINGS_VALUES,
  normalizeProfileSettings,
  profileSettingsToAPI,
  type ProfileSettingsData,
  type ProfileSettingsKeys,
} from '@/common/profileSettings.schema'

import { toast } from '@/components/ui/toaster/toasterHelpers'
import { ChangeEmailDialog } from '@/components/modals/ChangeEmailDialog'

import { ChangePasswordDialog } from '@/components/modals/ChangePasswordDialog'
import { getProfileSettings, updateProfileSettings } from '@/api/settings.api'
import { useUnsavedChangesGuardCustom } from '@/hooks/useUnsavedChangesGuard'
import { DismissChangesDialog } from '@/components/modals/DismissChangesDialog'
import { useUserStore } from '@/stores/user.store'
import { SettingsInput } from '../SettingsControls'
import { SettingsActionBar } from '../SettingsActionBar'
import { FormLayout } from '@/components/layout/FormLayout'

export function SubscriptionBillingDataPage() {
  const { setUser, user } = useUserStore()
  const [data, setData] = useState<ProfileSettingsData | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)

  const [initialValues, setInitialValues] = useState(INITIAL_PROFILE_SETTINGS_VALUES)

  const [isChangeEmailOpen, setIsChangeEmailOpen] = useState(false)

  const [isPasswordOpen, setPasswordOpen] = useState(false)

  const [openField, setOpenField] = useState<ProfileSettingsKeys | null>(null)

  // ===============================
  // LOAD PROFILE
  // ===============================
  useEffect(() => {
    let isMounted = true

    async function loadProfile() {
      try {
        setLoading(true)

        const json = await getProfileSettings()
        const normalized = normalizeProfileSettings(json)

        if (!isMounted) return

        setData(normalized)
        setInitialValues({ ...INITIAL_PROFILE_SETTINGS_VALUES, ...normalized })
      } catch {
        if (!isMounted) return
        toast.error('Fehler beim Laden')
        setError('Fehler beim Laden.')
      } finally {
        setLoading(false)
      }
    }

    loadProfile()
  }, [])

  // ===============================
  // FORM
  // ===============================
  const formik = useFormik<ProfileSettingsData>({
    initialValues,
    validationSchema: ProfileSettingsSchema,
    enableReinitialize: true,
    validateOnChange: true,
    validateOnBlur: true,
    validateOnMount: false,
    onSubmit: async values => {
      // const changed: Partial<ProfileSettingsData> = {}

      // ;(Object.keys(values) as ProfileSettingsKeys[]).forEach(key => {
      //   if (values[key] !== initialValues[key]) {
      //     changed[key] = values[key]
      //   }
      // })
      const changed: Partial<ProfileSettingsData> = {}

      ;(Object.keys(values) as ProfileSettingsKeys[]).forEach(key => {
        const value = values[key]
        const initialValue = initialValues[key]

        const hasChanged =
          Array.isArray(value) || Array.isArray(initialValue)
            ? JSON.stringify(value ?? []) !== JSON.stringify(initialValue ?? [])
            : value !== initialValue

        if (hasChanged) {
          ;(changed as Record<ProfileSettingsKeys, unknown>)[key] = value
        }
      })

      if (Object.keys(changed).length === 0) {
        toast.info('Keine Änderungen gefunden')
        return
      }

      try {
        setSaving(true)
        // Convert display values to API format before sending
        const apiPayload = profileSettingsToAPI(changed)
        await updateProfileSettings(apiPayload)
        toast.success('Änderungen gespeichert')
        const updated = normalizeProfileSettings({ ...initialValues, ...changed })
        setData(prev => (prev ? { ...prev, ...updated } : updated))
        setInitialValues(updated)

        if (user)
          setUser({
            ...user,
            displayName: `${updated.firstName} ${updated.lastName}`,
          })
        formik.resetForm({ values: updated })
      } catch (err: any) {
        // Handle backend validation errors
        if (err?.code === 'INVALID_PAYLOAD' && err?.errors) {
          // Set Formik errors from backend response
          const backendErrors: Record<string, string> = {}
          Object.keys(err.errors).forEach(key => {
            const errorMessages = err.errors[key]
            if (Array.isArray(errorMessages) && errorMessages.length > 0) {
              backendErrors[key] = errorMessages[0]
            }
          })
          formik.setErrors(backendErrors)

          // Mark fields as touched so errors show
          Object.keys(backendErrors).forEach(key => {
            formik.setFieldTouched(key, true)
          })

          toast.error('Validierungsfehler', 'Bitte überprüfen Sie die markierten Felder')
        } else {
          toast.error(err?.message || 'Fehler beim Speichern')
        }
      } finally {
        setSaving(false)
      }
    },
  })

  const { isOpen, confirmNavigation, cancelNavigation } = useUnsavedChangesGuardCustom(
    formik.dirty
  )
  const [cancelOpen, setCancelOpen] = useState(false)

  const showActionBar = formik.dirty
  const saveDisabled = !formik.dirty || !formik.isValid || saving
  function resetToInitial() {
    formik.resetForm({ values: initialValues })
    formik.setErrors({})
  }
  async function doSave() {
    await formik.submitForm()
  }

  // ===============================
  // SELECT COLLECTIONS
  // ===============================
  const salutationCollection = useMemo(
    () =>
      createListCollection({
        items: [
          { value: 'Herr', label: 'Herr' },
          { value: 'Frau', label: 'Frau' },
          { value: 'Andere', label: 'Andere' },
        ],
      }),
    []
  )

  const countryCollection = useMemo(
    () =>
      createListCollection({
        items: [
          { value: 'Deutschland', label: 'Deutschland' },
          { value: 'Österreich', label: 'Österreich' },
          { value: 'Schweiz', label: 'Schweiz' },
        ],
      }),
    []
  )

  // ===============================
  // SELECT COMPONENT
  // ===============================
  function FormikSelect({
    field,
    formik,
    collection,
    openField,
    setOpenField,
  }: {
    field: ProfileSettingsKeys
    formik: any
    collection: ListCollection<{ value: string; label: string }>
    openField: ProfileSettingsKeys | null
    setOpenField: (v: ProfileSettingsKeys | null) => void
  }) {
    const error = formik.touched[field] && formik.errors[field]
    const currentValue = formik.values[field]
    const isTopics = field === 'topics'

    const selectValue = Array.isArray(currentValue)
      ? currentValue
      : currentValue
        ? [currentValue]
        : []

    const selectedLabels = collection.items
      .filter(item => selectValue.includes(item.value))
      .map(item => item.label)

    return (
      <Select.Root
        collection={collection}
        value={selectValue}
        multiple={isTopics}
        onValueChange={({ value }) => {
          formik.setFieldValue(field, isTopics ? value : value[0])
          formik.setFieldTouched(field, true)

          if (!isTopics) {
            setOpenField(null)
          }
        }}
        positioning={{
          strategy: 'fixed',
          placement: 'bottom-start',
          sameWidth: true,
          offset: { mainAxis: 6, crossAxis: 0 },
        }}
        open={openField === field}
        onOpenChange={e => setOpenField(e.open ? field : null)}
        invalid={!!error}
      >
        <Select.Trigger w="full">
          {isTopics ? (
            <Text color={selectedLabels.length ? undefined : 'fg.muted'}>
              {selectedLabels.length ? selectedLabels.join(', ') : 'Bitte auswählen'}
            </Text>
          ) : (
            <Select.ValueText placeholder="Bitte auswählen" />
          )}
          <Select.Indicator />
        </Select.Trigger>

        <Select.Positioner>
          <Select.Content w="var(--reference-width)" maxH="260px" overflowY="auto">
            {collection.items.map(item => {
              const selected = selectValue.includes(item.value)

              return (
                <Select.Item key={item.value} item={item.value}>
                  <HStack justify="space-between" w="full">
                    <Text>{item.label}</Text>
                    {isTopics && selected && <Text>✓</Text>}
                  </HStack>
                </Select.Item>
              )
            })}
          </Select.Content>
        </Select.Positioner>
      </Select.Root>
    )
  }

  // ===============================
  // LOADING / ERROR
  // ===============================
  if (loading) {
    return (
      <Flex w="100%" h="300px" align="center" justify="center">
        <Spinner />
      </Flex>
    )
  }
  if (error) {
    toast.error(error)
    return (
      <Flex w="100%" h="300px" align="center" justify="center">
        <Text color="fg.error">{error}</Text>
      </Flex>
    )
  }

  // ===============================
  // RENDER
  // ===============================
  return (
    <>
      <DismissChangesDialog
        isOpen={isOpen}
        onClose={cancelNavigation}
        onDismiss={confirmNavigation}
        onSave={async () => {
          if (formik.dirty) await formik.submitForm()
          confirmNavigation()
        }}
        onNavigate={cancelNavigation}
      />
      <DismissChangesDialog
        isOpen={cancelOpen}
        onClose={() => setCancelOpen(false)}
        onDismiss={() => {
          resetToInitial()
          setCancelOpen(false)
        }}
        onSave={async () => {
          await doSave()
          setCancelOpen(false)
        }}
        onNavigate={() => {
          // kept for API compatibility
        }}
      />
      <chakra.form data-testid="subscription-billing-data" onSubmit={formik.handleSubmit}>
        <fieldset disabled={saving} style={{ width: '100%' }}>
          <FormLayout.Root>
            {/* SECTION: ADDRESS + PHONE */}
            <FormLayout.Section>
              <FormLayout.Title>Abonnementdaten &amp; Rechnung</FormLayout.Title>

              <FormLayout.Description>
                Hier verwalten Sie die Angaben für Ihr Abonnement im SIB Portal. Die hinterlegten
                Daten werden für die Vertragsführung und die Erstellung aller abonnementbezogenen
                Rechnungen verwendet.
              </FormLayout.Description>

              <Flex direction="column" gap={4}>
                <FormLayout.Title>Rechnungsadresse</FormLayout.Title>
                {/* Address grid */}
                <SimpleGrid columns={{ base: 1, sm: 2, lg: 5 }} gap={4}>
                  <Field.Root
                    position="relative"
                    invalid={!!(formik.touched.salutation && formik.errors.salutation)}
                  >
                    <Field.Label>
                      Anrede <Field.RequiredIndicator />
                    </Field.Label>
                    <FormikSelect
                      field="salutation"
                      formik={formik}
                      collection={salutationCollection}
                      openField={openField}
                      setOpenField={setOpenField}
                    />
                    {formik.touched.salutation && formik.errors.salutation && (
                      <Field.ErrorText>{formik.errors.salutation}</Field.ErrorText>
                    )}
                  </Field.Root>
                  <GridItem colSpan={{ base: 1, lg: 2 }}>
                    <Field.Root invalid={!!(formik.touched.firstName && formik.errors.firstName)}>
                      <Field.Label>
                        Vorname <Field.RequiredIndicator />
                      </Field.Label>
                      <SettingsInput
                        {...formik.getFieldProps('firstName')}
                        onChange={e => {
                          formik.handleChange(e)
                          if (!formik.touched.firstName) {
                            formik.setFieldTouched('firstName', true, false)
                          }
                        }}
                      />
                      {formik.touched.firstName && formik.errors.firstName && (
                        <Field.ErrorText>{formik.errors.firstName}</Field.ErrorText>
                      )}
                    </Field.Root>
                  </GridItem>

                  <GridItem colSpan={{ base: 1, lg: 2 }}>
                    <Field.Root invalid={!!(formik.touched.lastName && formik.errors.lastName)}>
                      <Field.Label>
                        Nachname <Field.RequiredIndicator />
                      </Field.Label>
                      <SettingsInput
                        {...formik.getFieldProps('lastName')}
                        onChange={e => {
                          formik.handleChange(e)
                          if (!formik.touched.lastName) {
                            formik.setFieldTouched('lastName', true, false)
                          }
                        }}
                      />
                      {formik.touched.lastName && formik.errors.lastName && (
                        <Field.ErrorText>{formik.errors.lastName}</Field.ErrorText>
                      )}
                    </Field.Root>
                  </GridItem>

                  <GridItem colSpan={{ base: 1, lg: 2 }}>
                    <Field.Root invalid={!!(formik.touched.company && formik.errors.company)}>
                      <Field.Label>Unternehmen</Field.Label>
                      <SettingsInput
                        {...formik.getFieldProps('company')}
                        onChange={e => {
                          formik.handleChange(e)
                          if (!formik.touched.company) {
                            formik.setFieldTouched('company', true, false)
                          }
                        }}
                      />
                      {formik.touched.company && formik.errors.company && (
                        <Field.ErrorText>{formik.errors.company}</Field.ErrorText>
                      )}
                    </Field.Root>
                  </GridItem>

                  <GridItem colSpan={{ base: 1, lg: 2 }}>
                    <Field.Root invalid={!!(formik.touched.street && formik.errors.street)}>
                      <Field.Label>
                        Straße <Field.RequiredIndicator />
                      </Field.Label>
                      <SettingsInput
                        {...formik.getFieldProps('street')}
                        onChange={e => {
                          const sanitized = e.target.value.replace(
                            /[^a-zA-Z0-9äöüßÄÖÜ\s\-./]/g,
                            ''
                          )

                          formik.setFieldValue('street', sanitized)

                          if (!formik.touched.street) {
                            formik.setFieldTouched('street', true, false)
                          }
                        }}
                      />
                      {formik.touched.street && formik.errors.street && (
                        <Field.ErrorText>{formik.errors.street}</Field.ErrorText>
                      )}
                    </Field.Root>
                  </GridItem>
                  <Field.Root
                    invalid={!!(formik.touched.streetNumber && formik.errors.streetNumber)}
                  >
                    <Field.Label>
                      Hausnummer <Field.RequiredIndicator />
                    </Field.Label>
                    <SettingsInput
                      {...formik.getFieldProps('streetNumber')}
                      onChange={e => {
                        formik.handleChange(e)
                        if (!formik.touched.streetNumber) {
                          formik.setFieldTouched('streetNumber', true, false)
                        }
                      }}
                    />
                    {formik.touched.streetNumber && formik.errors.streetNumber && (
                      <Field.ErrorText>{formik.errors.streetNumber}</Field.ErrorText>
                    )}
                  </Field.Root>

                  <Field.Root invalid={!!(formik.touched.postal && formik.errors.postal)}>
                    <Field.Label>
                      Postleitzahl <Field.RequiredIndicator />
                    </Field.Label>
                    <SettingsInput
                      {...formik.getFieldProps('postal')}
                      onChange={e => {
                        formik.handleChange(e)
                        if (!formik.touched.postal) {
                          formik.setFieldTouched('postal', true, false)
                        }
                      }}
                    />
                    {formik.touched.postal && formik.errors.postal && (
                      <Field.ErrorText>{formik.errors.postal}</Field.ErrorText>
                    )}
                  </Field.Root>

                  <GridItem colSpan={{ base: 1, lg: 2 }}>
                    <Field.Root invalid={!!(formik.touched.city && formik.errors.city)}>
                      <Field.Label>
                        Stadt <Field.RequiredIndicator />
                      </Field.Label>
                      <SettingsInput
                        {...formik.getFieldProps('city')}
                        onChange={e => {
                          const sanitized = e.target.value.replace(/[^a-zA-ZäöüßÄÖÜ\s\-']/g, '')

                          formik.setFieldValue('city', sanitized)

                          if (!formik.touched.city) {
                            formik.setFieldTouched('city', true, false)
                          }
                        }}
                      />
                      {formik.touched.city && formik.errors.city && (
                        <Field.ErrorText>{formik.errors.city}</Field.ErrorText>
                      )}
                    </Field.Root>
                  </GridItem>

                  <GridItem colSpan={{ base: 1, lg: 2 }}>
                    <Field.Root invalid={!!(formik.touched.country && formik.errors.country)}>
                      <Field.Label>
                        Land <Field.RequiredIndicator />
                      </Field.Label>
                      <FormikSelect
                        field="country"
                        formik={formik}
                        collection={countryCollection}
                        openField={openField}
                        setOpenField={setOpenField}
                      />
                      {formik.touched.country && formik.errors.country && (
                        <Field.ErrorText>{formik.errors.country}</Field.ErrorText>
                      )}
                    </Field.Root>
                  </GridItem>
                </SimpleGrid>

                {/* PHONE SECTION */}
                <SimpleGrid columns={{ base: 1, sm: 2, lg: 3 }} gap={4}>
                  <Field.Root
                    invalid={
                      !!(formik.touched.phone && formik.errors.phone) ||
                      !!(formik.touched.phonePrefix && formik.errors.phonePrefix)
                    }
                  >
                    <Field.Label>Telefon</Field.Label>
                    <HStack w="full">
                      <NativeSelect.Root width="fit-content">
                        <NativeSelect.Field
                          {...formik.getFieldProps('phonePrefix')}
                          onChange={e => {
                            formik.handleChange(e)
                            if (!formik.touched.phonePrefix) {
                              formik.setFieldTouched('phonePrefix', true, false)
                            }
                          }}
                        >
                          <option value="+49">+49</option>
                          <option value="+43">+43</option>
                          <option value="+41">+41</option>
                        </NativeSelect.Field>
                        <NativeSelect.Indicator />
                      </NativeSelect.Root>
                      <SettingsInput
                        {...formik.getFieldProps('phone')}
                        onChange={e => {
                          formik.handleChange(e)
                          if (!formik.touched.phone) {
                            formik.setFieldTouched('phone', true, false)
                          }
                        }}
                      />
                    </HStack>
                    {formik.touched.phonePrefix && formik.errors.phonePrefix && (
                      <Field.ErrorText>{formik.errors.phonePrefix}</Field.ErrorText>
                    )}
                    {formik.touched.phone && formik.errors.phone && (
                      <Field.ErrorText>{formik.errors.phone}</Field.ErrorText>
                    )}
                  </Field.Root>

                  <Field.Root
                    invalid={
                      !!(formik.touched.mobile && formik.errors.mobile) ||
                      !!(formik.touched.mobilePrefix && formik.errors.mobilePrefix)
                    }
                  >
                    <Field.Label>Mobil</Field.Label>
                    <HStack w="full">
                      <NativeSelect.Root width="fit-content">
                        <NativeSelect.Field
                          {...formik.getFieldProps('mobilePrefix')}
                          onChange={e => {
                            formik.handleChange(e)
                            if (!formik.touched.mobilePrefix) {
                              formik.setFieldTouched('mobilePrefix', true, false)
                            }
                          }}
                        >
                          <option value="+49">+49</option>
                          <option value="+43">+43</option>
                          <option value="+41">+41</option>
                        </NativeSelect.Field>
                        <NativeSelect.Indicator />
                      </NativeSelect.Root>
                      <SettingsInput
                        {...formik.getFieldProps('mobile')}
                        onChange={e => {
                          formik.handleChange(e)
                          if (!formik.touched.mobile) {
                            formik.setFieldTouched('mobile', true, false)
                          }
                        }}
                      />
                    </HStack>
                    {formik.touched.mobilePrefix && formik.errors.mobilePrefix && (
                      <Field.ErrorText>{formik.errors.mobilePrefix}</Field.ErrorText>
                    )}
                    {formik.touched.mobile && formik.errors.mobile && (
                      <Field.ErrorText>{formik.errors.mobile}</Field.ErrorText>
                    )}
                  </Field.Root>
                </SimpleGrid>
              </Flex>

              {/* VALIDATION ERROR SUMMARY */}
              {formik.dirty && !formik.isValid && Object.keys(formik.errors).length > 0 && (
                <Alert.Root status="warning">
                  <Alert.Indicator />
                  <Alert.Content>
                    <Alert.Title>Bitte korrigieren Sie folgende Fehler:</Alert.Title>
                    <Alert.Description>
                      {Object.keys(formik.errors).length} Feld(er){' '}
                      {Object.keys(formik.errors).length === 1 ? 'enthält' : 'enthalten'} Fehler
                    </Alert.Description>
                  </Alert.Content>
                </Alert.Root>
              )}
            </FormLayout.Section>
            <SettingsActionBar
              visible={showActionBar && !loading}
              onCancel={() => setCancelOpen(true)}
              cancelDisabled={saving}
              onSave={doSave}
              saveDisabled={saveDisabled}
              saving={saving}
            />
          </FormLayout.Root>
        </fieldset>
      </chakra.form>
      <ChangeEmailDialog
        isOpen={isChangeEmailOpen}
        onOpenChange={setIsChangeEmailOpen}
        currentEmail={data?.email || ''}
        onSuccess={(newEmail: string) => {
          setData(prev => (prev ? { ...prev, email: newEmail } : prev))
          formik.setFieldValue('email', newEmail)
        }}
      />
      <ChangePasswordDialog
        isOpen={isPasswordOpen}
        onOpenChange={setPasswordOpen}
        onSuccess={() => toast.success('Kennwort erfolgreich geändert')}
      />
    </>
  )
}
